import { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/atoms/Icon';
import Logo from '../components/atoms/Logo';

const VerifyCode = () => {
  const { verifyCode, isAuthenticated, isCodeSent, maskedEmail, loading, error, login, currentNit } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(3600);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (timeLeft > 0) {
      const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [timeLeft]);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  const formatTimeLeft = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedText.length === 6) {
      const newCode = pastedText.split('');
      setCode(newCode);
      inputRefs.current[5].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codeString = code.join('');

    if (codeString.length !== 6) {
      return;
    }

    const result = await verifyCode(codeString);
    if (!result.success) {
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0].focus();
    }
  };

  // Reenviar código
  const handleResendCode = async () => {
    if (currentNit) {
      await login(currentNit);
      setTimeLeft(3600);
    }
  };

  const isCodeComplete = code.every(d => d !== '');
  const isTimeExpired = timeLeft <= 0;

  return (
    <section className="relative min-h-screen flex items-center justify-center hero-login">
      {isAuthenticated && <Navigate to="/dashboard" replace />}
      {!isCodeSent && <Navigate to="/login" replace />}

      <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px] pointer-events-none" />

      <div className="relative w-full max-w-2xl px-6">
        <div className="flex flex-col items-center">
          <Logo size="2xl" />

          <div className="mt-6 items-center gap-3 x-5 py-2">
            <Logo.Pagos2 size="xl" />
          </div>

          <div className="mt-6 text-center">
            <h1 className="text-2xl font-bold text-blue-900/90 mb-2">
              Verificación de Código
            </h1>
            <p className="text-blue-900/70 mb-1">
              Hemos enviado un código de 6 dígitos a:
            </p>
            <p className="text-blue-900 font-semibold text-lg">
              {maskedEmail}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-8 w-full flex flex-col items-center"
            noValidate
          >
            <div className="flex gap-3 mb-6">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className={[
                    'w-12 h-16 rounded-lg text-center text-3xl font-bold',
                    'bg-sky-100 text-blue-900',
                    'border transition-colors duration-200',
                    'outline-none focus:ring-2 focus:ring-offset-2',
                    error && isCodeComplete ? 'border-red-400 focus:ring-red-300'
                      : digit
                        ? 'border-blue-400 focus:ring-blue-300'
                        : 'border-sky-200 focus:ring-blue-300'
                  ].join(' ')}
                />
              ))}
            </div>

            <div className="mb-4 flex items-center gap-2">
              <Icon
                name="clock"
                className={`h-4 w-4 ${isTimeExpired ? 'text-red-500' : 'text-blue-600'}`}
              />
              <span className={`text-sm font-medium ${isTimeExpired ? 'text-red-600' : 'text-blue-600'}`}>
                {isTimeExpired ? 'Código expirado' : `Expira en ${formatTimeLeft(timeLeft)}`}
              </span>
            </div>

            {error && (
              <div className="mb-5 w-full max-w-md rounded-xl bg-red-50 border border-red-300 px-4 py-3 text-red-800 text-sm flex items-start gap-3 shadow-sm">
                <Icon name="alert-circle" className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!isCodeComplete || isTimeExpired || loading}
              className={[
                "h-12 px-12 rounded-full font-semibold uppercase tracking-[0.25em]",
                "transition-all duration-200 ease-out shadow-md cursor-pointer",
                loading || !isCodeComplete || isTimeExpired
                  ? "border border-ec-primary-300 text-ec-primary-300 bg-white/80 cursor-not-allowed"
                  : "border border-ec-primary-500 text-ec-primary-600 bg-white hover:bg-ec-primary-500 hover:text-white",
                "disabled:cursor-not-allowed disabled:opacity-70",
              ].join(" ")}
            >
              {loading ? "VERIFICANDO…" : "VERIFICAR"}
            </button>

            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading || (!isTimeExpired && timeLeft > 3540)}
              className={[
                "mt-4 text-sm font-medium underline transition-colors",
                loading || (!isTimeExpired && timeLeft > 3540)
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-blue-600 hover:text-blue-800"
              ].join(" ")}
            >
              <Icon name="refresh-cw" className="h-4 w-4 inline mr-1" />
              Reenviar Código
            </button>

          </form>

          <div className="mt-8 w-full max-w-md rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100/50 px-5 py-4 text-blue-800 text-sm shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100">
                <Icon name="info" className="h-4 w-4 text-blue-600" />
              </div>
              <span className="font-semibold text-blue-900">¿No recibiste el código?</span>
            </div>
            <ul className="ml-1 space-y-2 text-xs leading-relaxed text-blue-800/90">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>Revisa tu carpeta de <strong>spam</strong> o correo no deseado.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>Verifica que tu email esté correctamente configurado.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>El código es válido por <strong>60 minutos</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>Puedes solicitar un nuevo código después de <strong>1 minuto</strong>.</span>
              </li>
            </ul>
          </div>

        </div>
      </div>

      <div className="fixed bottom-6 right-6 flex items-center space-x-3 z-50">
        <a
          href="https://wa.me/+573150981918?text=Hola,%20tengo%20problemas%20con%20el%20código%20de%20verificación"
          target="_blank"
          rel="noreferrer noopener"
          className="hidden sm:inline-block text-sm font-medium text-blue-900/80 hover:text-blue-900 transition-colors"
        >
          ¿Problemas con el código?
        </a>

        <a
          href="https://wa.me/+573150981918?text=Hola,%20tengo%20problemas%20con%20el%20código%20de%20verificación"
          target="_blank"
          rel="noreferrer noopener"
          className="flex items-center justify-center h-14 w-14 rounded-full bg-green-500 text-white shadow-lg hover:bg-green-600 hover:scale-105 transition-transform duration-200"
          aria-label="WhatsApp"
        >
          <Icon legacyIcon="IconWhp.png" className="h-7 w-7" />
        </a>
      </div>

    </section>
  );
};

export default VerifyCode;